import { createSlice, createAsyncThunk, isAllOf } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import httpService from '../../common/http.service'

const RELIGION_STORAGE_KEY = 'RELIGION_LIST'

const getReligionFromLocal = () => {
	try {
		const data = localStorage.getItem(RELIGION_STORAGE_KEY)
		return data ? JSON.parse(data) : null
	} catch (error) {
		return null
	}
}

const setReligionToLocal = data => {
	localStorage.setItem(RELIGION_STORAGE_KEY, JSON.stringify(data))
}

export const appCreateReligion = createAsyncThunk('appReligion/appCreateReligion', async params => {
	try {
		const response = await httpService.post('/religion', {}, params)

		if (response?.data) {
			toast.success('Religion created Successfully')

			// ✅ Update localStorage
			const oldData = getReligionFromLocal() || []
			setReligionToLocal([...oldData, response.data.data])

			params.navigate('/religion')
		}

		return response.data.data
	} catch (error) {
		toast.error(error?.message)
	}
})

export const appGetAllReligion = createAsyncThunk(
	'appReligion/appGetAllReligion',
	async (params = {}) => {
		try {
			const { page = 1, limit = 1000, loading = true } = params

			const cachedReligion = getReligionFromLocal()

			if (cachedReligion && cachedReligion.length) {
				return {
					data: cachedReligion,
					pagination: null,
					fromCache: true,
				}
			}

			const response = await httpService.get(
				`/religion?page=${page}&limit=${limit}`,
				{},
				{},
				{ loading },
			)

			setReligionToLocal(response.data.data)

			return {
				data: response.data.data,
				pagination: response.data.pagination,
				fromCache: false,
			}
		} catch (error) {
			toast.error(error?.message || 'Failed to fetch religions')
			throw error
		}
	},
)

export const appUpdateReligion = createAsyncThunk('appReligion/appUpdateReligion', async params => {
	try {
		const response = await httpService.put(`/religion/${params.id}`, {}, { name: params.name })

		if (response?.data) {
			toast.success('Religion Updated Successfully')

			const oldData = getReligionFromLocal() || []
			const updatedData = oldData.map(item =>
				item._id === response.data.data._id ? response.data.data : item,
			)
			setReligionToLocal(updatedData)

			params.navigate('/religion/1')
		}

		return response.data
	} catch (error) {
		toast.error(error?.message)
	}
})

export const appDeleteReligion = createAsyncThunk('appReligion/appDeleteReligion', async id => {
	try {
		const response = await httpService.delete(`/religion/${id}`)

		if (response?.data) {
			toast.success('Religion Deleted Successfully')

			const oldData = getReligionFromLocal() || []
			const filteredData = oldData.filter(item => item._id !== id)
			setReligionToLocal(filteredData)

			response.data.id = id
		}

		return response.data
	} catch (error) {
		toast.error(error?.message)
	}
})

export const appReligionSlice = createSlice({
	name: 'Religion',
	initialState: {
		religion: [],
		paginate: '',
		isloder: false,
		isdeleted: false,
	},
	reducers: {},
	extraReducers: builder => {
		builder.addMatcher(isAllOf(appGetAllReligion.pending), state => {
			state.isloder = true
		})

		builder.addMatcher(isAllOf(appGetAllReligion.fulfilled), (state, action) => {
			state.religion = action.payload.data
			state.isloder = false

			if (action.payload.pagination) {
				const p = action.payload.pagination
				state.paginate = {
					hasNextPage: p.totalPages > p.currentPage,
					hasPrevPage: p.currentPage > 1,
					limit: p.pageSize,
					nextPage: p.currentPage + 1,
					page: p.currentPage,
					pagingCounter: (p.currentPage - 1) * p.pageSize + 1,
					prevPage: p.currentPage - 1,
					totalDocs: p.totalRecords,
					totalPages: p.totalPages,
				}
			}
		})

		builder.addMatcher(isAllOf(appGetAllReligion.rejected), state => {
			state.isloder = false
		})

		builder.addMatcher(isAllOf(appDeleteReligion.pending), state => {
			state.isdeleted = false
		})

		builder.addMatcher(isAllOf(appDeleteReligion.fulfilled), (state, action) => {
			state.isdeleted = true
			state.religion = state.religion.filter(item => item._id !== action.payload.id)
		})

		builder.addMatcher(isAllOf(appDeleteReligion.rejected), state => {
			state.isdeleted = false
		})

		builder.addMatcher(isAllOf(appUpdateReligion.fulfilled), (state, action) => {
			const index = state.religion.findIndex(item => item._id === action.payload.data._id)
			if (index !== -1) {
				state.religion[index] = action.payload.data
			}
		})
	},
})

export default appReligionSlice.reducer
